import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Box } from "@mui/material";

import DeleteFromGroup from "../DeleteFromGroup";
import {
  IAccountGroup,
  IGetTradersSearchResponse,
} from "../../../../api/fundedCompliance/index.types";
import { FundedComplianceAPI, FUNDED_COMPLIANCE_API_URLS } from "../../../../api/fundedCompliance";
import { EditableContainerWithActions, SearchAutocomplete } from "../../../../components/ui";
import { useAdminProviderDataContext } from "../../../../context";

interface IProps {
  account: IAccountGroup["compliance_traders_accounts"][number];
}

const EditTraderAccounts = ({ account }: IProps): React.ReactElement => {
  const queryClient = useQueryClient();
  const {
    user: {
      allowed_features: { access_edit_compliance_entries },
    },
  } = useAdminProviderDataContext();

  const [alertErrorText, setAlertErrorText] = useState("");
  const [isShowAlert, setIsShowAlert] = useState(false);

  const [search, setSearch] = useState("");
  const [searchResult, setSearchResult] = useState<
    IGetTradersSearchResponse["data"]["funded_traders"]
  >([]);

  const [selectedAccount, setSelectedAccount] =
    useState<IGetTradersSearchResponse["data"]["funded_traders"][number]>();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };
  const onChange = (newValue: IGetTradersSearchResponse["data"]["funded_traders"][number]) => {
    setSelectedAccount(newValue);
  };

  useQuery({
    enabled: !!search,
    queryKey: [FUNDED_COMPLIANCE_API_URLS.tradersSearch, search],
    queryFn: async ({ signal }) => {
      const query = new URLSearchParams();
      query.set("email", search);
      const response = await FundedComplianceAPI.getTradersSearchRequest(query.toString(), signal);
      setSearchResult(response.data.funded_traders);
      return response;
    },
  });

  const updateGroupMemberMutation = useMutation({
    mutationFn: async () => {
      if (!selectedAccount) return;
      setLoading(true);
      await FundedComplianceAPI.changeGroupMemberRequest(account.id, {
        account_id: selectedAccount.account.id,
        email: selectedAccount.account.email,
      });
    },
    onSuccess: () => {
      setLoading(false);
      queryClient.invalidateQueries({
        queryKey: [FUNDED_COMPLIANCE_API_URLS.tradersDbList],
      });
    },
    onError: (error: { response: { data: { message: string } } }) => {
      setLoading(false);
      setIsShowAlert(true);
      setAlertErrorText(error.response.data.message);
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        ...(!access_edit_compliance_entries && { height: "26px" }),
        "& > div:last-child": {
          opacity: 0,
          visibility: "hidden",
          transition: "opacity 0.2s ease-in-out, visibility 0.2s ease-in-out",
        },
        "&:hover > div:last-child": {
          opacity: 1,
          visibility: "visible",
        },
      }}
    >
      <EditableContainerWithActions
        isShowAlert={isShowAlert}
        alertErrorText={alertErrorText}
        isChanged={!!selectedAccount}
        handleApply={() => updateGroupMemberMutation.mutate()}
        setIsShowAlert={() => {
          setIsShowAlert(false);
          setAlertErrorText("");
        }}
        isUpdating={false}
        isEditable={access_edit_compliance_entries}
        defaultValue={account?.account?.email || account?.submissions_outer_account?.email}
        ParentComponent="div"
        renderComponent={() => (
          <SearchAutocomplete
            handleChangeInput={handleChangeInput}
            label="Email"
            onChange={onChange}
            options={searchResult}
            getOptionLabel={(option) => option.account.email}
            getOptionSelected={(option) =>
              option.account.email === account?.account?.email ||
              option.account.email === account?.submissions_outer_account?.email
            }
            loading={loading}
            open={open}
            setOpen={setOpen}
          />
        )}
      />

      <Box>{access_edit_compliance_entries && <DeleteFromGroup account={account} />}</Box>
    </Box>
  );
};

export default EditTraderAccounts;
